<template>
  <div id="event-overview" class="brand-card-lg">
    <div v-if="initialLoading">
      <HeaderLoader />
    </div>
    <div v-else>
      <div class="sm:flex sm:items-center sm:justify-between space-y-3 pb-5 sm:space-y-0">
        <div class="space-x-6 flex items-center justify-between flex-grow">
          <p class="inline title">Event Dashboard</p>
          <el-button v-if="showExport" type="plain" class="border-brand-gray-blue border-2 px-4 rounded-md" @click="downloadEvents">
            <span class="text-xs text-brand-blue font-semibold">Export</span>
          </el-button>
        </div>
      </div>
      <div class="flex justify-between flex-grow">
        <div class="flex-col flex space-y-2">
          <div class="flex items-center space-x-2">
            <DatePickerCondensed @change="handleDateChange" :dateRange="[startDate, endDate]"></DatePickerCondensed>
            <!-- <SessionFilter @update="onFilterUpdate" :include="['trial', 'offerType', 'billingInterval', 'segmentId', 'response', 'planId']" /> -->
          </div>
        </div>
      </div>
    </div>
    <div>
      <div v-if="loading && !counts.length" class="-ml-4">
        <SessionListLoader />
      </div>
      <div v-else-if="counts.length && events.length" class="mt-6 grid grid-cols-3 gap-x-4 gap-y-6">
        <div v-for="(event, index) in events.slice(0, numChartsShown)" :key="event.event">
          <p class="header">{{ event.event }}</p>
          <p class="subheader">
            {{ event.count }} {{ event.count === 1 ? 'event' : 'events' }}
            <span v-if="event.uniqueCustomers || event.uniqueUsers"
              >from {{ event.uniqueCustomers || event.uniqueUsers }}
              {{
                event.uniqueCustomers ? (event.uniqueCustomers === 1 ? 'customer' : 'customers') : event.uniqueUsers === 1 ? 'user' : 'users'
              }}</span
            >
            in last 30 days
          </p>
          <!-- <p v-if="event.uniqueUsers" class="subheader">{{ event.uniqueUsers }} unique user(s) in last 30 days</p> -->
          <!-- <p class="subheader">{{ event.uniqueCustomers }} unique customer(s) in last 30 days</p> -->
          <div class="my-3" v-if="(event.uniqueCustomers && (totalUnique.ids || totalUnique.customers)) || (event.uniqueUsers && totalUnique.users)">
            <el-progress
              :percentage="
                event.uniqueCustomers
                  ? Math.round((event.uniqueCustomers / (totalUnique.ids || totalUnique.customers)) * 100)
                  : Math.round((event.uniqueUsers / totalUnique.users) * 100)
              "
              :stroke-width="10"
              class="w-full pr-2 unique-progress"
            ></el-progress>
          </div>
          <div class="mt-4">
            <VueVegaEmbed :spec="chartVegaSpecs[index]" width="auto" :height="180" />
          </div>
        </div>
        <div class="flex justify-left" v-if="events.length > numChartsShown">
          <el-button type="primary" class="px-8 rounded-md focus:outline-none" size="small" @click="numChartsShown += 3">
            <span class="text-sm text-white font-semibold">Load More Event Charts ↓</span>
          </el-button>
        </div>
        <div class="flex justify-left" v-else-if="events.length > 3">
          <el-button type="primary" class="px-8 rounded-md focus:outline-none" size="small" @click="numChartsShown = 3">
            <span class="text-sm text-white font-semibold">Hide Event Charts ↑</span>
          </el-button>
        </div>
      </div>
      <div v-else class="bg-white h-96"></div>
    </div>
  </div>
</template>

<script>
import { LOAD_STATE } from '@/store/shared';
import StatService from '@/api/stats.service';
import { getDateEndpoints } from '@/helpers/date-utils';
import DatePickerCondensed from '@/components/UI/DatePickerCondensed.vue';
// import SessionFilter from '@/components/CancelAnalytics/SessionFilter.vue';
import { timeDay } from 'd3-time';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import SessionListLoader from '@/components/UI/Loader/SessionListLoader.vue';
import { mapGetters } from 'vuex';
import { EVENT_PROP } from '@/helpers/stats-utils';
import { genBarSpec } from '@/components/Vis/Vega/Specs/BarSpec';
import VueVegaEmbed from '@/components/Vis/Vega/VueVegaEmbed.vue';
import { descending } from 'd3-array';

export default {
  name: 'EventOverview',
  components: {
    // SessionFilter,
    DatePickerCondensed,
    SessionListLoader,
    HeaderLoader,
    VueVegaEmbed,
  },
  props: {
    showExport: {
      type: Boolean,
      default: false,
    },
  },
  async created() {
    await this.fetchData();
    this.initialLoading = false;
  },
  data() {
    const defaultStartDate = timeDay.offset(new Date(), -30);
    const [startDate, endDate] = getDateEndpoints([defaultStartDate, new Date()]);
    return {
      filter: {},
      counts: [],
      dataLoadState: LOAD_STATE.UNLOADED,
      startDate,
      endDate,
      initialLoading: true,
      totalUnique: {
        customers: 0,
        ids: 0,
        users: 0,
      },
      eventUnique: {},
      numChartsShown: 3,
    };
  },
  computed: {
    ...mapGetters(['daysSinceOrgCreated', 'orgCreatedDate']),
    days() {
      // list of days from startDate to endDate in form YYYY-MM-DD
      const days = [];
      let day = new Date(this.startDate);
      while (day <= this.endDate) {
        days.push(day.toISOString().split('T')[0]);
        day = timeDay.offset(day, 1);
      }
      return days;
    },
    loading() {
      return this.dataLoadState !== LOAD_STATE.LOADED;
    },
    eventData() {
      return this.counts.reduce((acc, c) => {
        if (!acc[c.event]) {
          acc[c.event] = 0;
        }
        acc[c.event] += c.count;
        return acc;
      }, {});
    },
    events() {
      // return list from eventData sorted by count
      return Object.keys(this.eventData)
        .map((event) => {
          const uniqueCounts = this.eventUnique[event];
          const uniqueCustomers = uniqueCounts ? Math.max(0, uniqueCounts.id, uniqueCounts.customer) : 0;
          const uniqueUsers = uniqueCounts ? uniqueCounts.user || 0 : 0;
          return { event, count: this.eventData[event], uniqueCustomers, uniqueUsers };
        })
        .sort((a, b) => descending(a.count, b.count));
    },
    chartVegaSpecs() {
      if (this.events && this.events.length) {
        return this.events.map((event) => {
          // combine with days to get a list of { day, count, event }
          const eventItems = this.counts.filter((c) => c.event === event.event);
          const allDays = this.days.map((day) => {
            const dayItem = eventItems.find((c) => c.day === day);
            return {
              day,
              count: dayItem ? dayItem.count : 0,
              event: event.event,
            };
          });
          return genBarSpec({
            data: allDays,
            xField: 'day',
            xSortField: 'day',
            yField: 'count',
            colorField: 'event',
            xLabels: true,
            yLabels: true,
            barLabels: true,
            yFormat: '0.2s',
            tooltipFields: [
              { field: 'day', title: 'Day', type: 'nominal' },
              { field: 'count', title: 'Count', type: 'quantitative' },
            ],
            color: '#3c5ccf',
          });
        });
      }
      return null;
    },
  },
  methods: {
    async fetchData() {
      const breakdown = [EVENT_PROP.DAY, EVENT_PROP.EVENT_NAME];
      const mappedFilter = Object.keys(this.filter).reduce((acc, k) => {
        if (this.filter[k].length === 1) {
          acc[k] = this.filter[k][0];
        }
        return acc;
      }, {});
      mappedFilter.startDate = this.startDate;
      mappedFilter.endDate = this.endDate;
      try {
        this.dataLoadState = LOAD_STATE.LOADING;
        const [counts, totalUnique, eventUnique] = await Promise.all([
          StatService.countEvents({ filter: mappedFilter, breakdown }),
          StatService.countEvents({ filter: mappedFilter, breakdown: [] }),
          StatService.countEvents({ filter: mappedFilter, breakdown: [EVENT_PROP.EVENT_NAME] }),
        ]);
        this.counts = counts;
        if (totalUnique[0]) {
          this.totalUnique = {
            customers: totalUnique[0].uniqueCustomerIdCount,
            ids: totalUnique[0].uniqueIdCount,
            users: totalUnique[0].uniqueUserIdCount,
          };
        }
        this.eventUnique = eventUnique.reduce((acc, e) => {
          acc[e.event] = {
            customer: e.uniqueCustomerIdCount,
            id: e.uniqueIdCount,
            user: e.uniqueUserIdCount,
          };
          return acc;
        }, {});
        this.dataLoadState = LOAD_STATE.LOADED;
      } catch (e) {
        console.log(e);
        this.dataLoadState = LOAD_STATE.ERROR_LOADING;
      }
    },
    handleDateChange(value) {
      this.startDate = value[0];
      this.endDate = value[1];
      this.fetchData();
    },
    onFilterUpdate(newFilter) {
      this.filter = newFilter;
      this.fetchData();
    },
  },
};
</script>
<style lang="scss">
.activity-stream-search {
  @apply shadow-sm;
  @apply rounded-md;
  width: 20rem;
  .el-input__icon {
    @apply text-brand-gray-blue;
  }
  .el-input__inner {
    @apply border-brand-gray-blue;
    @apply rounded-lg;
    border-width: 2px;
    line-height: 44px;
    height: 44px;
    &::placeholder {
      @apply text-brand-gray-blue;
    }
  }
}
.unique-progress {
  .el-progress__text {
    font-size: 13px !important;
    @apply text-gray-600;
    @apply font-medium;
  }
}
</style>
